// 261005 E3-P4b2 — nút "Phương án (tốn AI)" ở ②④⑤: dựng request autoSend cho khung Trợ lý. Hàm thuần, KHÔNG import antd/react (jest).
// Câu gửi là câu RIÊNG "Đưa ra 3 phương án…" (ghi đè câu hỏi "Giải thích…" của nút cũ — mode options không nên nhận hai lệnh trái nhau).
import { editBlockerText } from '../../episodeFixUtils';
import type { EpisodeScriptRecord } from '../../../../types/adaptation';
import { rowEditBlockerCode, type AssistantRequest } from './assistantIntent';

export const OPTIONS_LABEL = 'Phương án (tốn AI)';
export const OPTIONS_TOOLTIP = 'Gửi ngay cho AI: đề xuất 3 cách xử lý khác nhau (tốn AI)';
export const EPISODE_UNKNOWN_REASON = 'Không xác định được tập — mở tập thủ công';
export const STAGE_LOCKED_REASON = 'Bước đang khoá';

const MAX_SUBJECT = 300;
const clip = (s: string, n: number): string => (s.length > n ? `${s.slice(0, n)}…` : s);

/** Câu "3 phương án" cho một vấn đề (`subject` đã là cụm danh từ mô tả vấn đề). */
export const optionsText = (subject: string): string => `Đưa ra 3 phương án khác cách xử lý ${clip(subject.trim(), MAX_SUBJECT + 80)}.`;

/** Thêm `autoSend: 'options'` + câu 3 phương án. Không `problem` ⇒ giữ `suggestedText` của base (nếu có), không có nữa ⇒ câu chung. */
export const optionsRequest = (base: AssistantRequest, problem?: string): AssistantRequest => ({
  ...base,
  suggestedText: problem ? optionsText(problem) : (base.suggestedText ?? optionsText('vấn đề này')),
  autoSend: 'options',
});

/** Chủ đề cho cảnh báo có gate + message (② khung xương, ④ chia tập). */
export const warningSubject = (what: string, v: { gate: string; message: string }): string => `cảnh báo "${clip(String(v.message ?? ''), MAX_SUBJECT)}" của ${what}`;

/** ⑤ dòng "Cần xem": `base` = intent của `reasonAssistAction` (đã có episode + issueIds). */
export const reasonOptionsRequest = (base: AssistantRequest, ep: number, text: string): AssistantRequest =>
  optionsRequest(base, `lỗi của tập ${ep}: "${clip(String(text ?? '').trim(), MAX_SUBJECT)}"`);

/** Vấn đề của cổng AI: `episode_split` ⇒ chỉ chữ (không có chip); `localize` ⇒ null (nút tắt — chưa có trợ lý ③). */
export const gateIssueOptionsRequest = (stepKey: 'episode_split' | 'localize', issue: { where: string; problem: string }): AssistantRequest | null => {
  if (stepKey !== 'episode_split') return null;
  return optionsRequest({ stageKey: 'split', issueIds: [] }, `vấn đề cổng AI chia tập: ${clip(String(issue.problem ?? ''), MAX_SUBJECT)} (${issue.where})`);
};

/**
 * Mảng song song với `dedupeViolations(violations)`: mỗi phần tử = id chip BE `violation:<chỉ số GỐC của lần đầu>:<gate>`; `CRITIC` ⇒ null.
 * Khoá gộp phải giống `dedupeViolations` (`gate|message|superseded`, giữ thứ tự xuất hiện đầu).
 */
export const scriptViolationChipIds = (violations: ReadonlyArray<{ gate: string; message: string; superseded?: boolean }> | null | undefined): Array<string | null> => {
  const seen = new Set<string>();
  const out: Array<string | null> = [];
  (violations ?? []).forEach((v, idx) => {
    const key = `${v.gate}|${v.message}|${v.superseded === true ? 'S' : ''}`;   // 261015 P2b-2 (B2): cùng khoá với `dedupeViolations`
    if (seen.has(key)) return;
    seen.add(key);
    out.push(v.gate === 'CRITIC' ? null : `violation:${idx}:${v.gate}`);
  });
  return out;
};

/** Một vi phạm trong ScriptReader (tập `episode`) ⇒ request; id null (CRITIC) ⇒ null (không nút). */
export const scriptViolationOptionsRequest = (episode: number, chipId: string | null, v: { gate: string; message: string }): AssistantRequest | null =>
  chipId ? optionsRequest({ episode, issueIds: [chipId] }, `lỗi của tập ${episode}: "${clip(String(v.message ?? ''), MAX_SUBJECT)}"`) : null;

/**
 * 261015 P1 (F67 giữ) — hàng nào của ⑤ được nút "Phương án": CHỈ lỗi thật (`defect`, tra chip id) và dòng điểm `CRITIC` (kể cả khi nằm ở khối gập
 * "Thông báo & điểm", kind `score`). Mọi hàng không-phải-lỗi khác (thông báo/chưa kiểm/cần chạy lại/…) ⇒ null (không nút).
 */
export const scriptRowOptionsRequest = (
  episode: number | undefined,
  v: { gate: string; message: string; kind?: string },
  chipId: string | null,
): AssistantRequest | null => {
  if (!episode) return null;
  if (v.gate === 'CRITIC') return criticOptionsRequest(episode, v.message);
  if ((v.kind ?? 'defect') !== 'defect') return null;
  return scriptViolationOptionsRequest(episode, chipId, v);
};

export interface CriticDimension { label: string; score: number }

/** F67 — lấy 1–2 chiều thấp nhất của câu Coverage do BE dựng, không lấy "tổng thể" vì đó không phải chiều để sửa cảnh/thoại. */
export const lowestCriticDimensions = (message: string): CriticDimension[] => {
  const labels: Record<string, string> = {
    premise: 'premise', plot: 'plot', 'nhân vật': 'nhân vật', character: 'nhân vật', characters: 'nhân vật',
    'thoại': 'thoại', dialogue: 'thoại', 'bối cảnh': 'bối cảnh', setting: 'bối cảnh', hook: 'hook',
    'mở tập': 'mở tập', opening: 'mở tập',
  };
  const found: CriticDimension[] = [];
  const re = /(premise|plot|nhân vật|character(?:s)?|thoại|dialogue|bối cảnh|setting|hook|mở tập|opening)\s+(\d+(?:[.,]\d+)?)/gi;
  let match: RegExpExecArray | null;
  while ((match = re.exec(String(message ?? ''))) !== null) {
    const score = Number(match[2].replace(',', '.'));
    if (Number.isFinite(score)) found.push({ label: labels[match[1].toLowerCase()] ?? match[1], score });
  }
  return found.sort((a, b) => a.score - b.score).slice(0, 2);
};

/** F67 — CRITIC không có chip id BE, nhưng vẫn mở đúng tập, tự gửi và nói rõ chiều cần nâng. */
export const criticOptionsRequest = (episode: number, message: string): AssistantRequest | null => {
  if (!Number.isInteger(episode) || episode < 1) return null;
  const dimensions = lowestCriticDimensions(message);
  const subject = dimensions.length
    ? `điểm giám khảo AI thấp ở ${dimensions.map((d) => `${d.label} ${d.score}/10`).join(', ')}; đưa 3 phương án nâng điểm các chiều đó bằng các lệnh sửa cảnh/thoại hiện có`
    : 'nhận xét của giám khảo AI; đưa 3 phương án nâng điểm bằng các lệnh sửa cảnh/thoại hiện có';
  return optionsRequest({ episode, issueIds: [] }, subject);
};

/** "AI đọc cả mùa": `unitExact` ⇒ tick chip; không ⇒ chỉ chữ. `episode < 1` ⇒ null (nút tắt, `EPISODE_UNKNOWN_REASON`). */
export const seasonIssueOptionsRequest = (issue: { id: string; episode: number; unitExact: boolean; problem: string }): AssistantRequest | null => {
  if (!Number.isInteger(issue.episode) || issue.episode < 1) return null;
  const base: AssistantRequest = { episode: issue.episode, issueIds: issue.unitExact ? [issue.id] : [] };
  return optionsRequest(base, `lỗi liên tập do AI đọc cả mùa ở tập ${issue.episode}: "${clip(String(issue.problem ?? ''), MAX_SUBJECT)}"`);
};

/** Lý do tắt nút: chặng khoá. (Job đang chạy KHÔNG tắt — khung Trợ lý tự chặn gửi và nói lý do.) */
export const optionsDisabledReason = (o: { stageLocked?: boolean; extra?: string | null }): string | undefined =>
  o.extra || (o.stageLocked ? STAGE_LOCKED_REASON : undefined);

/**
 * Lý do tắt nút "Phương án" của MỘT tập (Drawer "AI đọc cả mùa"): chặng ⑤ khoá · tập đang sửa tay · tập không sửa được
 * (cùng luật `rowEditBlockerCode` của `reasonAssistAction`). Có lý do ⇒ nút tắt thay vì đóng Drawer rồi khung lặng lẽ bỏ qua intent.
 */
export const seasonIssueDisabledReason = (o: { stageLocked: boolean; script: Pick<EpisodeScriptRecord, 'status' | 'script'> | undefined | null; editing?: boolean }): string | undefined => {
  if (o.stageLocked) return STAGE_LOCKED_REASON;
  if (o.editing) return 'Đang sửa tay — Lưu hoặc Huỷ trước';
  return editBlockerText(rowEditBlockerCode(o.script)) ?? undefined;
};
